import React, { useMemo } from 'react';
import { Tag } from 'antd';
import type { TagProps as AntTagProps } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { useDictOptions } from '../../base/hooks/useApiService';
import { findDictOptionByValue } from '../../config';
import { useResolveTokenColor } from '../../utils/resolveTokenColor';
import { useResolvedDataSet } from '../../base/DataSetInheritContext';
import { prepareAntdDomProps } from '../../utils/normalizeReactStyle';
import type { BaseComponentProps, DataSetConfig } from '../../types/common';

// 默认颜色映射
const DEFAULT_COLOR_MAP: Record<string, string> = {
  '0': 'default',
  '1': 'success',
  '2': 'warning',
  '3': 'error',
  'true': 'success',
  'false': 'default',
  'yes': 'success',
  'no': 'default',
};

const TAG_EXTRA_SKIP = ['content', 'closeAction', 'autoColor', 'colorMap', 'bordered'] as const;

export interface AndTagProps extends AntTagProps, BaseComponentProps, DataSetConfig {
  /** 标签文本内容 */
  content?: string;
  /** 字典编码 - 用于将 value 转换为 label 显示 */
  dictCode?: string;
  /** 是否根据值自动设置颜色 */
  autoColor?: boolean;
  /** 自定义颜色映射 { value: color } 或 JSON 字符串 */
  colorMap?: Record<string, string> | string;
  /** 关闭动作 */
  closeAction?: string;
  /** 点击动作 */
  clickAction?: string;
}

export const AndTag: React.FC<AndTagProps> = ({
  id, 
  sessionId, 
  develop, 
  children, 
  content, 
  dataSet, 
  dataSetField, 
  dictCode,
  autoColor = false,
  colorMap: propColorMap,
  color: propColor,
  // Filter out custom props that shouldn't be passed to DOM
  closeAction: _closeAction,
  clickAction: _clickAction,
  ...restProps
}) => {
  const resolved = useResolvedDataSet(dataSet, dataSetField, sessionId);
  const effectiveDataSet = resolved.dataSet;
  const effectiveDataSetField = resolved.dataSetField;
  const effectiveSessionId = resolved.sessionId;

  const dataSetKey = useMemo(
    () => getDataSetStoreKey(effectiveSessionId, effectiveDataSet),
    [effectiveSessionId, effectiveDataSet]
  );

  // 使用 dictCode 加载字典数据
  const { options: dictOptions } = useDictOptions(dictCode);

  // 解析 colorMap（支持 JSON 字符串）
  const colorMap = useMemo((): Record<string, string> | undefined => {
    if (!propColorMap) return undefined;
    if (typeof propColorMap === 'string') {
      try {
        return JSON.parse(propColorMap);
      } catch {
        return undefined;
      }
    }
    return propColorMap;
  }, [propColorMap]);

  const storeProps = useAndStore((s) =>
    effectiveSessionId && id
      ? s.propsStore[`${effectiveSessionId}_props`]?.[id] ?? null
      : null
  );

  const storeValue = useAndStore((s) =>
    dataSetKey && effectiveDataSetField
      ? s.getDataSet(dataSetKey, effectiveDataSetField)
      : undefined
  );

  // 获取原始值
  const rawValue = useMemo(() => {
    if (develop) return content;
    if (storeValue !== undefined && storeValue !== null && storeValue !== '') {
      return String(storeValue);
    }
    if (storeProps?.content !== undefined) return storeProps.content;
    return content;
  }, [develop, storeValue, storeProps?.content, content]);

  // 字典转换：将 value 转换为 label
  const displayContent = useMemo(() => {
    if (dictCode && dictOptions.length > 0 && rawValue !== undefined && rawValue !== null) {
      const item = findDictOptionByValue(dictOptions, rawValue);
      return item?.label ?? rawValue;
    }
    return rawValue;
  }, [dictCode, dictOptions, rawValue]);

  // 自动颜色：根据值确定颜色
  const autoTagColor = useMemo(() => {
    if (!autoColor || rawValue === undefined || rawValue === null) return undefined;
    const valueStr = String(rawValue).toLowerCase();
    // 优先使用自定义颜色映射
    if (colorMap && colorMap[valueStr]) {
      return colorMap[valueStr];
    }
    // 其次使用默认映射
    return DEFAULT_COLOR_MAP[valueStr];
  }, [autoColor, rawValue, colorMap]);

  const mergedProps = useMemo(() => {
    const baseProps = { ...restProps };
    if (storeProps && typeof storeProps === 'object') {
      Object.keys(storeProps).forEach((key) => {
        if (!['content', 'color'].includes(key) && storeProps[key] !== undefined && storeProps[key] !== null) {
          (baseProps as any)[key] = storeProps[key];
        }
      });
    }
    return baseProps;
  }, [restProps, storeProps]);

  const tagProps = useMemo(
    () => prepareAntdDomProps(mergedProps as Record<string, unknown>, TAG_EXTRA_SKIP),
    [mergedProps],
  );

  // 解析 Design Token 颜色
  const effectiveColor = storeProps?.color ?? propColor;
  const resolvedColor = useResolveTokenColor(effectiveColor);

  // 最终颜色：优先使用解析后的颜色，其次使用自动颜色
  const finalColor = resolvedColor || autoTagColor;

  return (
    <Tag {...tagProps} color={finalColor}>
      {displayContent || children}
    </Tag>
  );
};

export default AndTag;
